import './UsersList.sass';

import React, { memo } from 'react';
import { Col, Row } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import Select from 'react-select';

import NumberUtils from '../../lib/NumberUtils';
import { canPerformAction } from '../../utilities/permissionUtils';
import Spinner from '../Spinner/Spinner';
import UserRow from './UserRow/UserRow';

function renderUsers(
  users,
  updateProfile,
  profile,
  toggleNew,
  newStudent,
  setSelectedUser,
  loadUserStatistics,
) {
  if (users?.length > 0) {
    return users.map((user) => (
      <UserRow
        key={NumberUtils.obtainUniqueKey()}
        user={user}
        updateProfile={updateProfile}
        selected={profile && user ? profile.id === user.id : false}
        toggleNew={toggleNew}
        newStudent={newStudent}
        setSelectedUser={setSelectedUser}
        loadUserStatistics={loadUserStatistics || (() => {})}
      />
    ));
  }
  return (
    <div className="text-center mt-4 text-light" style={{ fontSize: '25px ' }}>
      No users found.
    </div>
  );
}

function renderLoading() {
  return <Spinner />;
}

function renderTable(
  loading,
  users,
  updateProfile,
  profile,
  toggleNew,
  newStudent,
  setSelectedUser,
  loadUserStatistics,
) {
  return (
    <div className="table">
      {loading ? renderLoading() : ''}
      <div>
        {renderUsers(
          users,
          updateProfile,
          profile,
          toggleNew,
          newStudent,
          setSelectedUser,
          loadUserStatistics,
        )}
      </div>
    </div>
  );
}

const areMoreUser = (totalPagination, users, loading) => {
  if (totalPagination && users && users.length && !loading) {
    return totalPagination > users.length;
  }
};

const DEFAULT_FILTERS = {
  search: '',
  email: '',
  name: '',
  lastName: '',
  filterBySubscription: '',
  filterByRate: '',
  sortBy: 'lastName',
};

const SUBSCRIPTION_OPTIONS = [
  { value: 'Apple', label: 'Apple' },
  { value: 'Stripe', label: 'Stripe' },
  { value: 'Free', label: 'Free' },
];

const SORT_OPTIONS = [
  { value: 'lastName', label: 'Last Name' },
  { value: 'name', label: 'First Name' },
  { value: 'email', label: 'Email' },
];

const normalizeTextValue = (value) => value.replace(/\s+/g, ' ');

const getRateOption = (rate) => ({
  value: rate.id,
  label: rate.shortname ? rate.shortname : rate.name,
});

const getRateOptions = (rates) =>
  rates.filter((rate) => rate.name !== 'PFE').map(getRateOption);

const getSelectedOption = (options, value) =>
  options.find((option) => String(option.value) === String(value)) || null;

function UsersListStateless({
  users,
  forAdmin,
  updateProfile,
  filters,
  search,
  filterBySubscription,
  filterByRate,
  setSearch,
  loading,
  rates = [],
  changePage,
  page,
  totalPagination,
  profile,
  toggleNew,
  newStudent,
  setSelectedUser,
  loadUserStatistics,
  permissions,
}) {
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
  const usersListItemsRef = React.useRef(null);
  const usersTableRef = React.useRef(null);

  const userFilters = {
    ...DEFAULT_FILTERS,
    search: search || '',
    filterBySubscription: filterBySubscription || '',
    filterByRate: filterByRate || '',
    ...filters,
  };

  const rateOptions = getRateOptions(rates);
  const selectedRateOption = getSelectedOption(
    rateOptions,
    userFilters.filterByRate,
  );
  const selectedSubscriptionOption = getSelectedOption(
    SUBSCRIPTION_OPTIONS,
    userFilters.filterBySubscription,
  );
  const selectedSortOption =
    getSelectedOption(SORT_OPTIONS, userFilters.sortBy) || SORT_OPTIONS[0];

  const canCreate =
    (!forAdmin && canPerformAction('Accounts.Students.Manage', permissions)) ||
    (forAdmin && canPerformAction('Accounts.Admins.Manage', permissions));

  const setStudentFilters = (updatedFilters) => {
    setSearch({ ...userFilters, ...updatedFilters });
  };

  const handleSearchChange = (e) => {
    const normalizedValue = normalizeTextValue(e.target.value);
    if (forAdmin) {
      setSearch(normalizedValue, filterBySubscription, filterByRate);
      return;
    }
    setStudentFilters({ search: normalizedValue });
  };

  const handleTextFilterChange = (field) => (event) => {
    setStudentFilters({ [field]: normalizeTextValue(event.target.value) });
  };

  const clearStudentFilters = () => {
    setSearch(DEFAULT_FILTERS);
  };

  const clearStudentFilter = (field) => {
    setStudentFilters({
      [field]: field === 'sortBy' ? DEFAULT_FILTERS.sortBy : '',
    });
  };

  const activeFilterChips = [
    userFilters.search && {
      field: 'search',
      label: `Search: ${userFilters.search}`,
    },
    userFilters.email && {
      field: 'email',
      label: `Email: ${userFilters.email}`,
    },
    userFilters.name && {
      field: 'name',
      label: `First Name: ${userFilters.name}`,
    },
    userFilters.lastName && {
      field: 'lastName',
      label: `Last Name: ${userFilters.lastName}`,
    },
    userFilters.filterByRate && {
      field: 'filterByRate',
      label: `Rate: ${selectedRateOption ? selectedRateOption.label : userFilters.filterByRate}`,
    },
    userFilters.filterBySubscription && {
      field: 'filterBySubscription',
      label: `Subscription: ${userFilters.filterBySubscription}`,
    },
    userFilters.sortBy !== DEFAULT_FILTERS.sortBy && {
      field: 'sortBy',
      label: `Sort: ${selectedSortOption.label}`,
    },
  ].filter(Boolean);

  React.useLayoutEffect(() => {
    if (forAdmin || !usersTableRef.current) return undefined;

    const updateUsersTableHeight = () => {
      const tableTop = usersTableRef.current.getBoundingClientRect().top;
      usersTableRef.current.style.height = `${Math.max(
        window.innerHeight - tableTop,
        0,
      )}px`;
    };
    const resizeObserver = new ResizeObserver(updateUsersTableHeight);

    updateUsersTableHeight();
    window.addEventListener('resize', updateUsersTableHeight);
    if (usersListItemsRef.current) {
      resizeObserver.observe(usersListItemsRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateUsersTableHeight);
      resizeObserver.disconnect();
    };
  }, [forAdmin]);

  const renderCreateButton = () =>
    canCreate && <i onClick={() => toggleNew()} className="fa fa-plus fa-lg" />;

  const renderLegacySearch = () => (
    <Row className="usersListItemsRow">
      <Col className="noPadRight" xs={9} md={9}>
        <div className="searchInput">
          <input
            id="input"
            className="form-control"
            value={search}
            onChange={handleSearchChange}
            onPaste={handleSearchChange}
          />
          {search ? (
            <i
              onClick={() => setSearch('', filterBySubscription, filterByRate)}
              className="fa fa-close fa-lg"
            />
          ) : (
            <i className="fa fa-search fa-lg" />
          )}
        </div>
      </Col>
      <Col xs={2} md={2} className="plusContainer">
        {renderCreateButton()}
      </Col>
    </Row>
  );

  const renderStudentSearchPanel = () => (
    <>
      <Row className="usersListItemsRow studentSearchHeader">
        <Col className="noPadRight" xs={9} md={9}>
          <button
            type="button"
            className={
              isFiltersOpen
                ? 'searchFiltersToggle active'
                : 'searchFiltersToggle'
            }
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            <span>
              <i className="fa fa-search" />
              Search & filters
            </span>
            <span className="searchFiltersToggleMeta">
              {activeFilterChips.length > 0 && (
                <span className="activeFiltersCount">
                  {activeFilterChips.length}
                </span>
              )}
              <i
                className={
                  isFiltersOpen ? 'fa fa-chevron-up' : 'fa fa-chevron-down'
                }
              />
            </span>
          </button>
        </Col>
        <Col xs={2} md={2} className="plusContainer">
          {renderCreateButton()}
        </Col>
      </Row>

      {activeFilterChips.length > 0 && (
        <div className="filterChipsRow">
          {activeFilterChips.map((chip) => (
            <button
              type="button"
              className="filterChip"
              key={chip.field}
              onClick={() => clearStudentFilter(chip.field)}
            >
              <span>{chip.label}</span>
              <i className="fa fa-close" />
            </button>
          ))}
        </div>
      )}

      {isFiltersOpen && (
        <div className="searchFiltersPanel">
          {/* General search */}
          <div className="filterField filterFieldFull">
            <label htmlFor="student-general-search">General search</label>
            <input
              id="student-general-search"
              className="form-control"
              value={userFilters.search || ''}
              onChange={handleTextFilterChange('search')}
            />
          </div>

          {/* First Name + Last Name */}
          <div className="filterField">
            <label htmlFor="student-first-name-search">First Name</label>
            <input
              id="student-first-name-search"
              className="form-control"
              value={userFilters.name || ''}
              onChange={handleTextFilterChange('name')}
            />
          </div>
          <div className="filterField">
            <label htmlFor="student-last-name-search">Last Name</label>
            <input
              id="student-last-name-search"
              className="form-control"
              value={userFilters.lastName || ''}
              onChange={handleTextFilterChange('lastName')}
            />
          </div>

          {/* Email + Sort by */}
          <div className="filterField">
            <label htmlFor="student-email-search">Email</label>
            <input
              id="student-email-search"
              className="form-control"
              value={userFilters.email || ''}
              onChange={handleTextFilterChange('email')}
            />
          </div>
          <div className="filterField">
            <label htmlFor="student-sort-order">Sort by</label>
            <Select
              inputId="student-sort-order"
              value={selectedSortOption}
              options={SORT_OPTIONS}
              placeholder="Sort by"
              styles={{
                singleValue: (base) => ({ ...base, color: '#3a3f44' }),
                placeholder: (base) => ({ ...base, color: '#3a3f44' }),
              }}
              onChange={(selectedOption) =>
                setStudentFilters({
                  sortBy: selectedOption
                    ? selectedOption.value
                    : DEFAULT_FILTERS.sortBy,
                })
              }
            />
          </div>

          {/* Rate + Subscription */}
          <div className="filterField">
            <label htmlFor="student-rate-filter">Filter by AFSC</label>
            <Select
              aria-label="Filter by AFSC"
              inputId="student-rate-filter"
              value={selectedRateOption}
              options={rateOptions}
              placeholder="All AFSCs"
              isClearable
              onChange={(selectedOption) =>
                setStudentFilters({
                  filterByRate: selectedOption ? selectedOption.value : '',
                })
              }
            />
          </div>
          <div className="filterField">
            <label htmlFor="student-subscription-filter">
              Filter by subscription
            </label>
            <Select
              aria-label="Filter by subscription"
              inputId="student-subscription-filter"
              value={selectedSubscriptionOption}
              options={SUBSCRIPTION_OPTIONS}
              placeholder="All subscriptions"
              isClearable
              onChange={(selectedOption) =>
                setStudentFilters({
                  filterBySubscription: selectedOption
                    ? selectedOption.value
                    : '',
                })
              }
            />
          </div>

          {/* Row 5: Actions */}
          <div className="filterActions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={clearStudentFilters}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setIsFiltersOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div>
      <div ref={usersListItemsRef} className="usersListItems">
        {forAdmin ? renderLegacySearch() : renderStudentSearchPanel()}
      </div>

      <div ref={usersTableRef} className="animated fadeIn usersTable">
        <div style={{ height: '100%', overflow: 'auto' }}>
          <InfiniteScroll
            pageStart={0}
            loadMore={() => changePage(page + 1)}
            hasMore={!loading && areMoreUser(totalPagination, users, loading)}
            useWindow={false}
          >
            {renderTable(
              loading,
              users,
              updateProfile,
              profile,
              toggleNew,
              newStudent,
              setSelectedUser,
              loadUserStatistics,
            )}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
}

export default memo(UsersListStateless);
